import { useState, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Camera, Upload, Box, Fingerprint, RefreshCw, Copy, ExternalLink,
  CheckCircle, Package, Ruler, Scale, Video, Sparkles, Wand2, Aperture,
  X, AlertTriangle, Image as ImageIcon, ArrowRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiErrorMessage } from '@/lib/apiError';
import CamaraEnVivo, { esComputadora } from '@/components/CamaraEnVivo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Weight conversion utilities
const OZ_TO_LB = (oz) => oz / 16;
const OZ_TO_KG = (oz) => oz / 35.274;
const formatWeight = (oz, unit) => {
  if (oz == null) return '—';
  if (unit === 'oz') return `${Number(oz).toFixed(1)} oz`;
  if (unit === 'lb') return `${OZ_TO_LB(oz).toFixed(2)} lb`;
  if (unit === 'kg') return `${OZ_TO_KG(oz).toFixed(2)} kg`;
  if (unit === 'lb_oz') {
    const lb = Math.floor(OZ_TO_LB(oz));
    const rem = oz - lb * 16;
    return `${lb} lb ${rem.toFixed(1)} oz`;
  }
  return `${oz} oz`;
};

const ClickPicPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const verifyFileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  // Lo que va a Rate Shop: lo que estimo la IA, corregido por el comerciante.
  const [paquete, setPaquete] = useState(null);
  // Sube con cada foto nueva: la respuesta que llega tarde de la foto anterior se descarta.
  const fotoActual = useRef(0);
  const [camaraAbierta, setCamaraAbierta] = useState(false);

  // Verify state — now photo-based instead of hash paste
  const [verifyFile, setVerifyFile] = useState(null);
  const [verifyPreview, setVerifyPreview] = useState(null);
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifying, setVerifying] = useState(false);

  // Weight unit toggle
  const [weightUnit, setWeightUnit] = useState('lb_oz');

  // AI describe
  const [aiHint, setAiHint] = useState('');
  const [describing, setDescribing] = useState(false);
  const [aiDesc, setAiDesc] = useState(null);

  // ---- Sora video generation state ----
  const [videoKind, setVideoKind] = useState('pic_n_click');
  const [videoDesc, setVideoDesc] = useState('');
  const [videoSeconds, setVideoSeconds] = useState(8);
  const [videoJob, setVideoJob] = useState(null);
  const [videoBusy, setVideoBusy] = useState(false);
  const [videoErr, setVideoErr] = useState('');

  const usarFoto = (f) => {
    if (f) {
      fotoActual.current += 1;
      setFile(f);
      setResult(null);
      setPaquete(null);
      setAiDesc(null);
      setLoading(false);
      setDescribing(false);
      const reader = new FileReader();
      reader.onload = (ev) => setPreview(ev.target.result);
      reader.readAsDataURL(f);
    }
  };

  const handleFileChange = (e) => usarFoto(e.target.files?.[0]);

  const clearFile = () => {
    fotoActual.current += 1;
    setFile(null);
    setPreview(null);
    setResult(null);
    setPaquete(null);
    setAiDesc(null);
    setLoading(false);
    setDescribing(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const takePhoto = () => {
    // En la computadora el navegador ignora capture="environment" y abre los
    // archivos (Carlos, 30-oct): ahi la foto se saca con la webcam en la pagina.
    // En el telefono, capture abre su camara trasera.
    if (esComputadora()) { setCamaraAbierta(true); return; }
    if (cameraInputRef.current) cameraInputRef.current.click();
  };

  // Si la camara del telefono abre en negro (Mike, 29-oct), la foto se saca
  // con la camara del telefono y se elige de la galeria.
  const choosePhoto = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const paqueteCompleto = paquete && ['length', 'width', 'height', 'weight_oz'].every(k => Number(paquete[k]) > 0);

  const usarEnRateShop = () => {
    navigate('/dashboard/rate-shop', {
      state: {
        parcel: {
          length: Number(paquete.length),
          width: Number(paquete.width),
          height: Number(paquete.height),
          weight_oz: Number(paquete.weight_oz),
        },
      },
    });
  };

  const handleVerifyFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      setVerifyFile(f);
      setVerifyResult(null);
      const reader = new FileReader();
      reader.onload = (ev) => setVerifyPreview(ev.target.result);
      reader.readAsDataURL(f);
    }
  };

  const clearVerifyFile = () => {
    setVerifyFile(null);
    setVerifyPreview(null);
    setVerifyResult(null);
    if (verifyFileRef.current) verifyFileRef.current.value = '';
  };

  const analyzePic = async () => {
    if (!file) return;
    const foto = fotoActual.current;
    setLoading(true);
    setResult(null);
    setPaquete(null);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await fetch(`${API_URL}/api/shipping/click-pic`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const r = await res.json().catch(() => null);
      if (foto !== fotoActual.current) return;
      if (res.ok) {
        setResult(r);
        setPaquete({
          length: r.dimensions.length,
          width: r.dimensions.width,
          height: r.dimensions.height,
          weight_oz: r.weight.estimated_oz,
        });
      } else {
        setResult({ error: apiErrorMessage(r, `Analyze failed (HTTP ${res.status}). Try a clearer photo.`) });
      }
    } catch (err) {
      if (foto !== fotoActual.current) return;
      console.error('Click-pic error:', err);
      setResult({ error: err.message || 'Network error while analyzing photo.' });
    } finally {
      if (foto === fotoActual.current) setLoading(false);
    }
  };

  const verifyProduct = async () => {
    if (!verifyFile) return;
    setVerifying(true);
    setVerifyResult(null);
    try {
      const fd = new FormData();
      fd.append('image', verifyFile);
      const r = await fetch(`${API_URL}/api/shipping/click-pic/verify-photo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        throw new Error(j.detail || `Verify failed (HTTP ${r.status})`);
      }
      setVerifyResult({ ...j, extracted_hash: j.image_hash });
    } catch (err) {
      setVerifyResult({ verified: false, message: err.message || 'Verify failed' });
    } finally {
      setVerifying(false);
    }
  };

  // ---- AI describe ----
  const describeItem = async () => {
    if (!file) return;
    const foto = fotoActual.current;
    setDescribing(true); setAiDesc(null);
    try {
      const fd = new FormData();
      fd.append('image', file);
      if (aiHint) fd.append('hint', aiHint);
      const r = await fetch(`${API_URL}/api/click-pic/describe`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const j = await r.json().catch(() => null);
      if (foto !== fotoActual.current) return;
      if (r.ok) setAiDesc(j);
      else setAiDesc({ description: `Error: ${apiErrorMessage(j, 'AI describe failed')}` });
    } catch (e) {
      if (foto !== fotoActual.current) return;
      setAiDesc({ description: `Error: ${e.message}` });
    } finally {
      if (foto === fotoActual.current) setDescribing(false);
    }
  };

  const copyVerifyLink = () => {
    if (!result?.blockchain?.fingerprint) return;
    const url = `${window.location.origin}/verify/${result.blockchain.fingerprint}`;
    navigator.clipboard.writeText(url);
  };

  // ---- Sora video generation ----
  const [videoUrl, setVideoUrl] = useState(null);

  const fetchVideoBlob = async (downloadUrl) => {
    try {
      const r = await fetch(`${API_URL}${downloadUrl}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return;
      const blob = await r.blob();
      setVideoUrl(URL.createObjectURL(blob));
    } catch (e) { /* noop */ }
  };

  const pollJob = async (jobId) => {
    for (let i = 0; i < 90; i++) { // 90 * 4s = 6 min
      await new Promise(r => setTimeout(r, 4000));
      try {
        const r = await fetch(`${API_URL}/api/video/jobs/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const j = await r.json();
        setVideoJob(j);
        if (j.status === 'completed' && j.download_url) {
          await fetchVideoBlob(j.download_url);
          return j;
        }
        if (j.status === 'failed') return j;
      } catch (e) { /* keep polling */ }
    }
    return null;
  };

  const generateVideo = async () => {
    if (!videoDesc || videoDesc.length < 8) {
      setVideoErr('Description must be at least 8 characters.');
      return;
    }
    setVideoBusy(true); setVideoErr(''); setVideoJob(null); setVideoUrl(null);
    try {
      let res;
      if (file) {
        const fd = new FormData();
        fd.append('kind', videoKind);
        fd.append('description', videoDesc);
        fd.append('seconds', String(videoSeconds));
        fd.append('size', '1280x720');
        fd.append('image', file);
        res = await fetch(`${API_URL}/api/video/generate-with-image`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
      } else {
        res = await fetch(`${API_URL}/api/video/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            kind: videoKind, description: videoDesc,
            seconds: videoSeconds, size: '1280x720', model: 'sora-2',
          }),
        });
      }
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || 'Failed to queue video');
      setVideoJob({ ...j, status: 'queued' });
      await pollJob(j.job_id);
    } catch (e) {
      setVideoErr(e.message);
    } finally {
      setVideoBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="click-pic-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Click Pic</h1>
        <p className="text-slate-500 mt-1">AI-powered package dimension estimation + tamper-evident product fingerprint</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload Panel */}
        <div className="space-y-5">
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2"><Camera className="w-4 h-4 text-blue-600" /> Capture Item</h3>
            
            {/* Drop zone */}
            <label className={`block border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors relative ${
              preview ? 'border-blue-300 bg-blue-50/50 dark:bg-blue-900/10' : 'border-slate-200 dark:border-slate-700 hover:border-blue-300'
            }`} data-testid="upload-zone">
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" data-testid="file-input" />
              {preview ? (
                <>
                  <img src={preview} alt="Preview" className="max-h-48 mx-auto rounded-lg object-contain" />
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); clearFile(); }}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-rose-500 hover:bg-rose-600 text-white flex items-center justify-center shadow-lg"
                    title="Remove photo"
                    data-testid="delete-photo-btn"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <div>
                  <Upload className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="font-medium text-sm">Drop an image or click to upload</p>
                  <p className="text-xs text-slate-400 mt-1">Supports JPG, PNG, WEBP</p>
                </div>
              )}
            </label>

            {/* Camera capture button (mobile-first) */}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileChange}
              ref={cameraInputRef}
              className="hidden"
              data-testid="camera-input"
            />
            <div className="grid grid-cols-2 gap-2 mt-3">
              <Button
                type="button"
                variant="outline"
                onClick={takePhoto}
                data-testid="camera-btn"
              >
                <Aperture className="w-4 h-4 mr-2" /> Take photo
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={choosePhoto}
                data-testid="gallery-btn"
              >
                <ImageIcon className="w-4 h-4 mr-2" /> Choose photo
              </Button>
            </div>
            <p className="text-[11px] text-slate-500 mt-2" data-testid="camera-tip">
              Camera shows a black screen? Take the photo with your phone&apos;s camera, then tap Choose photo.
              Opening SelleSend in Safari or Chrome, not inside another app, can also help.
            </p>
            {camaraAbierta && (
              <CamaraEnVivo
                onFoto={(f) => { setCamaraAbierta(false); usarFoto(f); }}
                onCerrar={() => setCamaraAbierta(false)}
                onElegirFoto={() => { setCamaraAbierta(false); choosePhoto(); }}
              />
            )}
            <Button onClick={analyzePic} disabled={!file || loading} className="w-full mt-3" data-testid="analyze-btn">
              {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Box className="w-4 h-4 mr-2" />}
              {loading ? 'Analyzing…' : 'Analyze'}
            </Button>

            {/* AI Describe */}
            {file && (
              <div className="mt-4 p-3 rounded-lg border border-indigo-200 bg-indigo-50/40 dark:bg-indigo-950/20" data-testid="ai-describe-panel">
                <div className="flex items-center gap-2 mb-2">
                  <Wand2 className="w-4 h-4 text-indigo-500" />
                  <p className="text-xs font-semibold text-indigo-700">Describe with SelleAI</p>
                </div>
                <div className="flex gap-2">
                  <input
                    value={aiHint}
                    onChange={e => setAiHint(e.target.value)}
                    placeholder="Optional hint (e.g. model number, material)"
                    className="flex-1 min-w-0 h-9 px-2.5 rounded-md border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm"
                    data-testid="ai-hint"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={describeItem}
                    disabled={describing}
                    className="border-indigo-400 text-indigo-700"
                    data-testid="ai-describe-btn"
                  >
                    {describing ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <><Sparkles className="w-3.5 h-3.5 mr-1" />Describe</>}
                  </Button>
                </div>
                {aiDesc && (
                  <div className="mt-2 space-y-1.5 text-xs" data-testid="ai-desc-result">
                    <p><b>Description:</b> {aiDesc.description}</p>
                    {aiDesc.marketing_copy && <p><b>Marketing:</b> <em>{aiDesc.marketing_copy}</em></p>}
                    {aiDesc.suggested_category && <p><b>Category:</b> {aiDesc.suggested_category}</p>}
                    {aiDesc.seo_keywords?.length > 0 && (
                      <div className="flex gap-1 flex-wrap pt-1">
                        {aiDesc.seo_keywords.map((k, i) => (
                          <span key={i} className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-mono">{k}</span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Verificacion por foto contra el libro de registros */}
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6">
            <h3 className="font-semibold mb-1 flex items-center gap-2"><Fingerprint className="w-4 h-4 text-emerald-600" /> Verify Product</h3>
            <p className="text-xs text-slate-500 mb-4">
              Upload the same photo file that was registered. We check its fingerprint against the record ledger.
              A new photo of the item will not match: the fingerprint is of the file, not of the item.
            </p>

            <label className={`block border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors relative ${
              verifyPreview ? 'border-emerald-300 bg-emerald-50/40 dark:bg-emerald-900/10' : 'border-slate-200 dark:border-slate-700 hover:border-emerald-300'
            }`} data-testid="verify-upload-zone">
              <input ref={verifyFileRef} type="file" accept="image/*" onChange={handleVerifyFileChange} className="hidden" data-testid="verify-file-input" />
              {verifyPreview ? (
                <>
                  <img src={verifyPreview} alt="Verify preview" className="max-h-32 mx-auto rounded-lg object-contain" />
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); clearVerifyFile(); }}
                    className="absolute top-2 right-2 w-6 h-6 rounded-full bg-rose-500 hover:bg-rose-600 text-white flex items-center justify-center shadow"
                    title="Remove photo"
                    data-testid="delete-verify-photo-btn"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </>
              ) : (
                <div>
                  <Fingerprint className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm font-medium">Upload the registered photo to verify</p>
                  <p className="text-xs text-slate-400 mt-1">The system will fingerprint it and check the ledger</p>
                </div>
              )}
            </label>

            <Button
              onClick={verifyProduct}
              disabled={!verifyFile || verifying}
              className="w-full mt-3 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
              data-testid="verify-btn"
            >
              {verifying ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Verifying…</> : <><Fingerprint className="w-4 h-4 mr-2" />Fingerprint &amp; Verify</>}
            </Button>

            {verifyResult && (
              <div className={`mt-3 p-3 rounded-lg text-sm ${verifyResult.verified ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300' : 'bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300'}`} data-testid="verify-result">
                <div className="flex items-center gap-2">
                  {verifyResult.verified ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                  <span className="font-medium">{verifyResult.message || (verifyResult.verified ? 'Product verified on chain.' : 'Not found on chain.')}</span>
                </div>
                {verifyResult.extracted_hash && (
                  <p className="text-xs mt-1 font-mono opacity-75 truncate">Fingerprint: {verifyResult.extracted_hash}</p>
                )}
                {verifyResult.block_id && (
                  <p className="text-xs mt-1 font-mono opacity-75">Record id: {verifyResult.block_id}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Results */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6">
          {loading && (
            <div className="flex flex-col items-center justify-center py-16">
              <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mb-4" />
              <p className="text-slate-500 text-sm">AI analyzing image dimensions...</p>
            </div>
          )}

          {result && result.error && (
            <div className="p-4 rounded-lg bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 text-sm" data-testid="clickpic-error">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className="w-4 h-4" />
                <span className="font-semibold">Analysis failed</span>
              </div>
              <p className="text-xs">{result.error}</p>
            </div>
          )}

          {result && !result.error && (
            <div className="space-y-5" data-testid="clickpic-result">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Analysis Result</h3>
                {result.confidence != null && (
                  <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 font-medium">
                    AI confidence {Math.round(result.confidence * 100)}%
                  </span>
                )}
              </div>

              {/* Dimensions */}
              <div>
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-3">Estimated Dimensions</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800 text-center">
                    <Ruler className="w-5 h-5 text-blue-500 mx-auto mb-2" />
                    <p className="text-2xl font-semibold tabular-nums">{result.dimensions.length}"</p>
                    <p className="text-xs text-slate-400">Length</p>
                  </div>
                  <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800 text-center">
                    <Ruler className="w-5 h-5 text-purple-500 mx-auto mb-2" />
                    <p className="text-2xl font-semibold tabular-nums">{result.dimensions.width}"</p>
                    <p className="text-xs text-slate-400">Width</p>
                  </div>
                  <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800 text-center">
                    <Ruler className="w-5 h-5 text-amber-500 mx-auto mb-2" />
                    <p className="text-2xl font-semibold tabular-nums">{result.dimensions.height}"</p>
                    <p className="text-xs text-slate-400">Height</p>
                  </div>
                </div>
              </div>

              {/* Weight */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Weight Analysis</p>
                  <div className="flex gap-1" data-testid="weight-unit-toggle">
                    {[
                      { id: 'oz',    label: 'oz' },
                      { id: 'lb',    label: 'lb' },
                      { id: 'lb_oz', label: 'lb+oz' },
                      { id: 'kg',    label: 'kg' },
                    ].map(u => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => setWeightUnit(u.id)}
                        data-testid={`wu-${u.id}`}
                        className={`text-[10px] px-2 py-0.5 rounded-md font-mono uppercase ${weightUnit === u.id ? 'bg-blue-500 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-500'}`}
                      >
                        {u.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
                    <p className="text-xs text-slate-400 mb-1">Actual</p>
                    <p className="font-semibold tabular-nums">{formatWeight(result.weight.estimated_oz, weightUnit)}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
                    <p className="text-xs text-slate-400 mb-1">Dim Weight</p>
                    <p className="font-semibold tabular-nums">{formatWeight(result.weight.dim_weight_oz, weightUnit)}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                    <p className="text-xs text-blue-400 mb-1">Billable</p>
                    <p className="font-semibold text-blue-700 dark:text-blue-300 tabular-nums">{formatWeight(result.weight.billable_oz, weightUnit)}</p>
                  </div>
                </div>
                <div className="mt-2 p-2.5 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-[11px] text-amber-800 dark:text-amber-200 flex items-start gap-1.5" data-testid="dim-disclaimer">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>
                    <b>Estimate only.</b> Dimensional weight and billable charges are AI approximations. Actual carrier fees will vary based on the parcel measured at pickup, surcharges, and residential/commercial classification.
                  </span>
                </div>
              </div>

              {/* Lo que va a la etiqueta: el comerciante confirma o corrige (§9.126) */}
              {paquete && (
                <div className="p-4 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-950/20" data-testid="clickpic-paquete">
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">For the label</p>
                  <p className="text-[11px] text-slate-500 mb-3">
                    Check the size and weight and fix them if needed. Carriers charge by the real size and weight.
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[
                      ['length', 'Length (in)'],
                      ['width', 'Width (in)'],
                      ['height', 'Height (in)'],
                      ['weight_oz', 'Weight (oz)'],
                    ].map(([k, label]) => (
                      <label key={k} className="text-[11px] text-slate-500">
                        {label}
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={paquete[k]}
                          onChange={e => setPaquete(p => ({ ...p, [k]: e.target.value }))}
                          className="mt-1 w-full h-9 px-2 rounded-md border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm tabular-nums"
                          data-testid={`paquete-${k}`}
                        />
                      </label>
                    ))}
                  </div>
                  <Button className="w-full mt-3" onClick={usarEnRateShop} disabled={!paqueteCompleto} data-testid="use-in-rate-shop">
                    Use in Rate Shop <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}

              {/* Category */}
              <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-slate-500" />
                  <div>
                    <p className="text-xs text-slate-400">Item Category</p>
                    <p className="font-medium">{result.category ? result.category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Not sure'}</p>
                  </div>
                </div>
              </div>

              {/* La huella y su anclaje */}
              <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Product Fingerprint</p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={copyVerifyLink} className="h-7 text-xs" data-testid="copy-verify-btn">
                      <Copy className="w-3 h-3 mr-1" /> Copy buyer link
                    </Button>
                    <a
                      href={`/verify/${result.blockchain.fingerprint}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                      data-testid="open-verify-btn"
                    >
                      Open <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-slate-900 dark:bg-slate-950 text-emerald-400 font-mono text-xs space-y-1.5 break-all">
                  <p>Hash: {result.blockchain.fingerprint}</p>
                  <p>Record id: {result.blockchain.block_id}</p>
                  <p>Ledger: SelleSend record ledger (not a public blockchain)</p>
                  <p className="flex items-center gap-1.5">
                    <CheckCircle className="w-3 h-3" /> Registered: {result.blockchain.registered ? 'TRUE' : 'FALSE'}
                  </p>
                </div>
                <p className="text-[11px] text-slate-500 mt-2">
                  Share the buyer link. Buyers open it without logging in and can check that this fingerprint was registered here, and when. It shows the photo has not changed since then.
                </p>
              </div>
            </div>
          )}

          {!result && !loading && (
            <div className="flex flex-col items-center justify-center py-16">
              <Camera className="w-12 h-12 text-slate-200 mb-4" />
              <h3 className="font-medium text-slate-400">Upload a photo</h3>
              <p className="text-sm text-slate-300 mt-1">AI will estimate dimensions and create a tamper-evident fingerprint</p>
            </div>
          )}
        </div>
      </div>

      {/* ==================== SORA VIDEO GENERATION ==================== */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6" data-testid="sora-video-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-fuchsia-500 to-pink-500 flex items-center justify-center">
            <Video className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold flex items-center gap-2">Sora Video Studio <Sparkles className="w-4 h-4 text-fuchsia-500" /></h3>
            <p className="text-xs text-slate-500">Turn your product photo into a listing or marketing clip. 4–12 seconds.</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Kind</label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                {[
                  { id: 'pic_n_click', label: 'Product listing' },
                  { id: 'marketing',   label: 'Marketing hero' },
                ].map(k => (
                  <button key={k.id} type="button" onClick={() => setVideoKind(k.id)}
                    data-testid={`video-kind-${k.id}`}
                    className={`p-2.5 rounded-lg border-2 text-xs font-medium ${videoKind === k.id ? 'border-fuchsia-500 bg-fuchsia-50 text-fuchsia-700' : 'border-slate-200 text-slate-500'}`}>
                    {k.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Describe your product</label>
              <textarea rows={4} value={videoDesc} onChange={e => setVideoDesc(e.target.value)}
                placeholder="Vintage leather Polaroid camera, brown, chrome accents"
                className="mt-1.5 w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 dark:bg-slate-800 text-sm"
                data-testid="video-desc" />
              <p className="text-xs text-slate-400 mt-1">
                {file ? 'Uploaded photo will anchor the shot.' : 'Tip: upload a photo above to anchor the shot.'}
              </p>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Length</label>
              <div className="flex gap-2 mt-1.5">
                {[4, 8, 12].map(s => (
                  <button key={s} type="button" onClick={() => setVideoSeconds(s)}
                    data-testid={`video-sec-${s}`}
                    className={`flex-1 py-2 rounded-lg border-2 text-xs font-semibold ${videoSeconds === s ? 'border-fuchsia-500 bg-fuchsia-50 text-fuchsia-700' : 'border-slate-200 text-slate-500'}`}>
                    {s}s
                  </button>
                ))}
              </div>
            </div>
            <Button onClick={generateVideo} disabled={videoBusy || !videoDesc}
              className="w-full bg-gradient-to-r from-fuchsia-500 to-pink-500 hover:from-fuchsia-600 hover:to-pink-600"
              data-testid="video-generate-btn">
              {videoBusy ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Rendering… (2-5 min)</> : <><Video className="w-4 h-4 mr-2" />Generate Video</>}
            </Button>
            {videoErr && <p className="text-xs text-rose-600" data-testid="video-error">{videoErr}</p>}
          </div>

          <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-4 min-h-[240px] flex items-center justify-center bg-slate-50 dark:bg-slate-950" data-testid="video-preview">
            {!videoJob && !videoBusy && (
              <div className="text-center text-slate-400 text-sm">
                <Video className="w-12 h-12 mx-auto mb-3 opacity-40" />
                Your video will appear here.
              </div>
            )}
            {videoJob && videoJob.status === 'queued' && (
              <div className="text-center">
                <RefreshCw className="w-8 h-8 text-fuchsia-500 mx-auto mb-2 animate-spin" />
                <p className="text-xs font-mono text-slate-500">job {videoJob.job_id?.slice(0, 8)}…</p>
                <p className="text-xs text-slate-400 mt-1">Sora is composing your clip.</p>
              </div>
            )}
            {videoJob && videoJob.status === 'completed' && videoUrl && (
              <video
                controls
                className="w-full rounded-lg"
                src={videoUrl}
                data-testid="generated-video"
              />
            )}
            {videoJob && videoJob.status === 'failed' && (
              <div className="text-rose-600 text-sm text-center">
                <p className="font-semibold">Generation failed</p>
                <p className="text-xs mt-1">{videoJob.error}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClickPicPage;
